<!--
Copyright 2024 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-account-edit" [ngStyle]="{'color': 'var(--mat-sys-primary)'}"></mat-icon>
  </div>
  <mat-form-field style="width: 300px; margin-left: 10px;">
    <input matInput [(ngModel)]="filterString" (keyup)="applyFilter($any($event.target).value)" placeholder="Search" />
    @if (filterString) {
    <button mat-icon-button matSuffix (click)="applyFilter('')" title="Clear Search">
      <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
    </button>
    }
  </mat-form-field>
  <div class="button-end">
    <mat-paginator #paginator [pageIndex]="0" [pageSize]="20"
      [pageSizeOptions]="[5, 10, 15, 20, 25, 50]"></mat-paginator>
  </div>
</div>

<mat-menu #addMenu="matMenu" [overlapTrigger]="false">
  @for (unit of getUnitList(); track unit.id) {
  <button mat-menu-item (click)="addUnitToMsel(unit.id)" title="Add {{ unit.name }} to MSEL">
    {{ unit.shortName }} - {{ unit.name }}
  </button>
  }
  @if (getUnitList().length === 0) {
  <button mat-menu-item disabled>No units available</button>
  }
</mat-menu>

@if (msel) {
<div class="frame">
  <div class="table-container">
    <table mat-table #contributorTable [dataSource]="mselUnitDataSource" matSort (matSortChange)="sortChanged($event)" multiTemplateDataRows>
      <!-- action column -->
      <ng-container matColumnDef="action">
        <th mat-header-cell *matHeaderCellDef class="column-action">
          @if (canManageMsel()) {
          <button mat-icon-button [matMenuTriggerFor]="addMenu" (menuOpened)="refreshUnits()" title="Add a contributor unit" style="outline: none;">
            <mat-icon class="mdi-24px center-self" fontIcon="mdi-plus-circle"></mat-icon>
          </button>
          }
        </th>
        <td mat-cell *matCellDef="let element" class="column-action">
          @if (canManageMsel()) {
          <button mat-icon-button (click)="removeUnitFromMsel(element.id); $event.stopPropagation();"
            title="Remove unit from MSEL">
            <mat-icon class="mdi-18px" fontIcon="mdi-minus-circle-outline"></mat-icon>
          </button>
          }
        </td>
      </ng-container>
      <!-- shortName Column -->
      <ng-container matColumnDef="shortName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-short-name">Short Name</th>
        <td mat-cell *matCellDef="let element" class="column-short-name">
          {{ element.unit?.shortName }}
        </td>
      </ng-container>
      <!-- name Column -->
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-name">Name</th>
        <td mat-cell *matCellDef="let element" class="column-name">
          {{ element.unit?.name }}
        </td>
      </ng-container>
      <!-- Expanded Content Column -->
      <ng-container matColumnDef="expandedDetail">
        <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
          <div [@detailExpand]="element.id == expandedElementId ? 'expanded' : 'collapsed'" style="overflow: hidden">
            @if (expandedElementId === element.id) {
            <div class="expanded-detail-div">
              @for (user of getMselUnitUsers(element.unitId); track user) {
              <div class="unit-detail">
                <div class="user-name four-cell">{{ user.name }}</div>
                <div class="role-select" (click)="$event.stopPropagation();">
                  <mat-form-field>
                    <mat-label>MSEL Roles</mat-label>
                    <mat-select multiple [value]="getSelectedMselRoles(user.id)"
                      (selectionChange)="setMselRoles(user.id, $event.value)"
                      [disabled]="!canManageMsel()">
                      @for (role of getMselRolesToDisplay(); track role) {
                      <mat-option [value]="role" [disabled]="isOwnOwnerRole(user.id, role) || isCreatorRole(user.id, role)">
                        <span [matTooltip]="getRoleDescription(role)"
                              matTooltipPosition="right"
                              style="pointer-events: auto; display: block; width: 100%;">
                          {{ role }}
                        </span>
                      </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                @if (msel.useCite) {
                <div class="role-select" (click)="$event.stopPropagation();">
                  <mat-form-field>
                    <mat-label>CITE Role</mat-label>
                    <mat-select [value]="getCiteEvaluationRole(user.id)"
                      (selectionChange)="setCiteEvaluationRole(user.id, $event.value)"
                      [disabled]="!canManageMsel()">
                      <mat-option [value]="null">&mdash;</mat-option>
                      @for (role of citeEvaluationRoles; track role) {
                      <mat-option [value]="role">{{ role }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                }
                @if (msel.useGallery) {
                <div class="role-select" (click)="$event.stopPropagation();">
                  <mat-form-field>
                    <mat-label>Gallery Role</mat-label>
                    <mat-select [value]="getGalleryExhibitRole(user.id)"
                      (selectionChange)="setGalleryExhibitRole(user.id, $event.value)"
                      [disabled]="!canManageMsel()">
                      <mat-option [value]="null">&mdash;</mat-option>
                      @for (role of galleryExhibitRoles; track role) {
                      <mat-option [value]="role">{{ role }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                }
                @if (msel.useSteamfitter) {
                <div class="role-select" (click)="$event.stopPropagation();">
                  <mat-form-field>
                    <mat-label>Steamfitter Role</mat-label>
                    <mat-select [value]="getSteamfitterScenarioRole(user.id)"
                      (selectionChange)="setSteamfitterScenarioRole(user.id, $event.value)"
                      [disabled]="!canManageMsel()">
                      <mat-option [value]="null">&mdash;</mat-option>
                      @for (role of steamfitterScenarioRoles; track role) {
                      <mat-option [value]="role">{{ role }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                }
              </div>
              }
              @if (getMselUnitUsers(element.unitId).length === 0) {
              <div class="unit-detail">This unit has no users.</div>
              }
            </div>
            }
          </div>
        </td>
      </ng-container>
      <!-- row definitions -->
      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class]="getRowClass(row.id)"
        (click)="rowClicked(row)"></tr>
      <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
    </table>
  </div>
</div>
}
